import moment from "moment";
import { getWeekPeriodFromDate } from "./nutrition-helpers";
import _ from "lodash";
import { MUSCLE_GROUP_ARRAY, OUTCOME_STATUS } from "../constants";
import { formattedTarget } from "./actuals";
import { getExerciseName } from "../parts/modals/learn-more/blockHelpers";

const { COMPLETED, MISSED } = OUTCOME_STATUS;

// Helper function to get complex reps string
const getComplexRepsString = (exercise, setIndex, outcomeIndex) => {
  if (!exercise.exercises || !Array.isArray(exercise.exercises)) {
    return null;
  }

  const repsArray = [];
  // Sort exercises by their order in the complex to ensure correct sequence
  const sortedExercises = [...exercise.exercises].sort((a, b) => {
    // If exercises have an order field, use that; otherwise maintain original order
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // Fallback to creation order or _id comparison
    return (a._id || "").localeCompare(b._id || "");
  });

  sortedExercises.forEach((nestedExercise) => {
    // Find the corresponding set in the nested exercise
    if (nestedExercise.sets && nestedExercise.sets[setIndex]) {
      const nestedSet = nestedExercise.sets[setIndex];
      if (nestedSet.outcomes && nestedSet.outcomes[outcomeIndex]) {
        const { outcome } = nestedSet.outcomes[outcomeIndex];
        if (outcome && outcome.reps) {
          repsArray.push(outcome.reps);
        }
      } else if (nestedSet.reps) {
        // Fallback to target reps if no outcome
        repsArray.push(nestedSet.reps);
      }
    }
  });

  return repsArray.length > 0 ? `(${repsArray.join("+")})` : null;
};

const groupExercisesByName = (exercises) => {
  const uniqueNames = {};
  const result = [];

  for (const obj of exercises) {
    if (!uniqueNames[getExerciseName(obj)]) {
      result.push({
        name: getExerciseName(obj),
        numberOfSets: [],
        sessions: 0,
      });
      uniqueNames[getExerciseName(obj)] = true;
    }
  }

  return result;
};

const getWeeklyDates = (from, to, startingCalendarDay) => {
  let weeklyPeriods = {};

  let { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
    from,
    startingCalendarDay
  );

  while (endOfWeek.isSameOrBefore(moment(to).add(7, "days"))) {
    const period = `${startOfWeek.format("YYYY-MM-DD")} - ${moment(
      endOfWeek
    ).format("YYYY-MM-DD")}`;

    weeklyPeriods[period] = {
      startOfWeek: startOfWeek.toISOString(),
      endOfWeek: endOfWeek.toISOString(),
      utcDate: Number(startOfWeek.format("YYYYMMDD")),
      maxWeight: 0,
      topE1rm: 0,
      volume: 0,
      sessions: 0,
      outcomes: {},
      hasData: false,
    };

    startOfWeek.add(7, "days");
    endOfWeek.add(7, "days");
  }

  return weeklyPeriods;
};

const getBlockExerciseData = (
  weekExercises,
  populatedExercises,
  startingCalendarDay,
  intensityMeasure,
  activeUser,
  withWeight,
  fromMetric
) => {
  for (const exercise of weekExercises) {
    const name = getExerciseName(exercise);

    // For complex exercises, use the first exercise's properties for defaults
    const baseExercise = exercise.exercises ? exercise.exercises[0] : exercise;
    const isComplex = !!baseExercise.complex;

    const defaultRepMax = _.get(
      baseExercise,
      baseExercise.type === "primary" && !isComplex
        ? `repMaximums.${baseExercise.primary}`
        : "secondaryRepMaximum",
      _.get(activeUser, "repMaximums.squat", 0)
    );

    const { units } = activeUser;
    const precision = units === "metric" ? 2 : 1;

    const isRiR = intensityMeasure === "RiR";

    const indexOfExercise = populatedExercises.findIndex(
      (item) => item.name === name
    );

    if (indexOfExercise >= 0) {
      const { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
        String(exercise.utcDate),
        startingCalendarDay
      );

      const period = `${startOfWeek.format("YYYY-MM-DD")} - ${moment(
        endOfWeek
      ).format("YYYY-MM-DD")}`;

      // Create object
      if (!populatedExercises[indexOfExercise].periods) {
        populatedExercises[indexOfExercise].periods = {};
      }

      // Deep clone weeklyPeriods for each exercise
      if (!populatedExercises[indexOfExercise].periods[period]) {
        populatedExercises[indexOfExercise].periods[period] = {
          startOfWeek: startOfWeek.toISOString(),
          maxWeight: 0,
          topE1rm: 0,
          volume: 0,
          sessions: 0,
          outcomes: {},
          hasData: false,
        };
        populatedExercises[indexOfExercise].periods[period].numberOfSets = [];
      }

      populatedExercises[indexOfExercise].periods[period].sessions += 1;

      if (
        populatedExercises[indexOfExercise].sessions <
        populatedExercises[indexOfExercise].periods[period].sessions
      ) {
        populatedExercises[indexOfExercise].sessions += 1;
      }

      const realNumberOfSets = [];
      let cummulativeSets = 0;

      // Handle sets for both complex and regular exercises
      const setsToProcess = exercise.sets;

      setsToProcess.forEach((set, setIndex) => {
        realNumberOfSets.push(Number(set.sets || set.rounds || 1));

        let maxWeight = 0;
        let topE1rm = 0;
        let volume = 0;

        // For complex exercises, aggregate metrics from all nested exercises
        if (exercise.exercises && Array.isArray(exercise.exercises)) {
          exercise.exercises.forEach((nestedExercise) => {
            if (nestedExercise.sets && nestedExercise.sets[setIndex]) {
              const nestedSet = nestedExercise.sets[setIndex];
              if (nestedSet.outcomes) {
                _.times(
                  Number(nestedSet.sets || nestedSet.rounds || 1)
                ).forEach((setOrRoundIndex) => {
                  if (nestedSet.outcomes[setOrRoundIndex]) {
                    const { outcome, status } =
                      nestedSet.outcomes[setOrRoundIndex];
                    if (status === COMPLETED) {
                      const { load, reps, e1rm } = outcome || {};
                      if (maxWeight < load) maxWeight = load;
                      if (topE1rm < e1rm) topE1rm = e1rm;
                      volume += Math.round(Number(load) * Number(reps) || 0);
                    }
                  }
                });
              }
            }
          });
        }

        if (set.outcomes) {
          // Update flag to show block of data or not
          populatedExercises[indexOfExercise].periods[period].hasData = true;

          // Fill the outcomes
          _.times(Number(set.sets || set.rounds || 1)).forEach(
            (setOrRoundIndex) => {
              if (set.outcomes[setOrRoundIndex]) {
                const { outcome, status } = set.outcomes[setOrRoundIndex];

                if (status === COMPLETED || status === MISSED) {
                  const { load, reps, e1rm } = outcome || {};

                  const percentRM = _.round(
                    (_.get(set, "load", 0) / defaultRepMax) * 100,
                    1
                  );

                  // Only calculate metrics for regular exercises (not complex)
                  // Complex exercise metrics are calculated above
                  if (
                    !exercise.exercises ||
                    !Array.isArray(exercise.exercises)
                  ) {
                    if (maxWeight < load && status === COMPLETED)
                      maxWeight = load;
                    if (topE1rm < e1rm && status === COMPLETED) topE1rm = e1rm;
                    if (status === COMPLETED)
                      volume += Math.round(Number(load) * Number(reps) || 0);
                  }

                  // Get complex reps if this is a complex exercise
                  const complexReps = getComplexRepsString(
                    exercise,
                    setIndex,
                    setOrRoundIndex
                  );

                  // For complex exercises, use the same trick as non-powerlifting:
                  // sets: null, powerlifting: false, and put complex reps in the reps field
                  const outcomeForTarget = complexReps
                    ? {
                        ...outcome,
                        sets: null,
                        rounds: null,
                        reps: complexReps,
                      }
                    : { ...outcome, sets: null, rounds: null };

                  populatedExercises[indexOfExercise].periods[period].outcomes =
                    {
                      ...populatedExercises[indexOfExercise].periods[period]
                        .outcomes,
                      [populatedExercises[indexOfExercise].periods[period]
                        .sessions]: {
                        ...populatedExercises[indexOfExercise].periods[period]
                          .outcomes[
                          populatedExercises[indexOfExercise].periods[period]
                            .sessions
                        ],
                        [cummulativeSets + setOrRoundIndex]: {
                          target: formattedTarget(
                            outcomeForTarget,
                            false,
                            percentRM,
                            "metric",
                            load
                              ? withWeight(
                                  _.round(fromMetric(load, units), precision),
                                  units
                                )
                              : 0,
                            intensityMeasure,
                            isRiR
                          ),
                          comment: set.comments
                            ? set.comments[setOrRoundIndex]
                            : null,
                          video: set.videos
                            ? set.videos[setOrRoundIndex]
                            : null,
                          status,
                          exerciseId: exercise._id,
                          utcDate: exercise.utcDate,
                          outcomeLoad: load || null,
                          outcomeReps: reps || null,
                          outcomeRpe: outcome?.rpe || null,
                          exerciseType: baseExercise.type,
                          exercisePrimary: baseExercise.primary,
                          exerciseSecondary: baseExercise.secondary,
                        },
                      },
                    };
                }
              } else {
                const { load } = set;

                const percentRM = _.round(
                  (_.get(set, "load", 0) / defaultRepMax) * 100,
                  1
                );

                // If outcome is missing, add placeholder
                // Get complex reps if this is a complex exercise (for targets without outcomes)
                const complexReps = getComplexRepsString(
                  exercise,
                  setIndex,
                  setOrRoundIndex
                );

                // For complex exercises, use the same trick as non-powerlifting:
                // sets: null, powerlifting: false, and put complex reps in the reps field
                const setForTarget = complexReps
                  ? { ...set, sets: null, rounds: null, reps: complexReps }
                  : { ...set, sets: null, rounds: null };

                populatedExercises[indexOfExercise].periods[period].outcomes = {
                  ...populatedExercises[indexOfExercise].periods[period]
                    .outcomes,
                  [populatedExercises[indexOfExercise].periods[period]
                    .sessions]: {
                    ...populatedExercises[indexOfExercise].periods[period]
                      .outcomes[
                      populatedExercises[indexOfExercise].periods[period]
                        .sessions
                    ],
                    [cummulativeSets + setOrRoundIndex]: {
                      target: formattedTarget(
                        setForTarget,
                        false,
                        percentRM,
                        "metric",
                        load
                          ? withWeight(
                              _.round(fromMetric(load, units), precision),
                              units
                            )
                          : 0,
                        intensityMeasure,
                        isRiR
                      ),
                      comment: null,
                      video: null,
                      status: "incomplete",
                      utcDate: exercise.utcDate,
                    },
                  },
                };
              }
            }
          );
        }

        // Update maxWeight, top e1rm and volume
        if (
          populatedExercises[indexOfExercise].periods[period].maxWeight <
          maxWeight
        )
          populatedExercises[indexOfExercise].periods[period].maxWeight =
            maxWeight;
        if (
          populatedExercises[indexOfExercise].periods[period].topE1rm < topE1rm
        )
          populatedExercises[indexOfExercise].periods[period].topE1rm = topE1rm;
        populatedExercises[indexOfExercise].periods[period].volume += volume;

        cummulativeSets += set.sets || set.rounds || 1;
      });

      const totalSets = realNumberOfSets.reduce(
        (acc, currentValue) => acc + currentValue,
        0
      );
      populatedExercises[indexOfExercise].periods[period].numberOfSets.push(
        totalSets
      );

      // Update numberOfSets array based on all periods
      const sessions =
        populatedExercises[indexOfExercise].periods[period].sessions;
      for (let i = 0; i < sessions; i++) {
        const setsArray = [];
        for (const key in populatedExercises[indexOfExercise].periods) {
          if (populatedExercises[indexOfExercise].periods.hasOwnProperty(key)) {
            const sets =
              populatedExercises[indexOfExercise].periods[key].numberOfSets;
            if (sets && sets[i]) {
              setsArray.push(sets[i]);
            }
          }
        }
        populatedExercises[indexOfExercise].numberOfSets[i] = Math.max(
          ...setsArray
        );
      }
    }
  }
};

const addMissingPeriods = (exercises, weeklyPeriods) => {
  exercises.forEach((exercise) => {
    const exercisePeriods = Object.keys(exercise.periods);
    Object.keys(weeklyPeriods).forEach((period) => {
      if (!exercisePeriods.includes(period)) {
        exercise.periods[period] = {
          startOfWeek: weeklyPeriods[period].startOfWeek,
          maxWeight: 0,
          topE1rm: 0,
          volume: 0,
          sessions: 0,
          outcomes: {},
          hasData: false,
        };
      }
    });
  });
};

const addMissingOutcomes = (exercises) => {
  exercises.forEach((exercise) => {
    _.times(exercise.sessions).forEach((session) => {
      for (const period in exercise.periods) {
        // Check if outcomes object exists, if not, initialize it
        if (!exercise.periods[period].outcomes) {
          exercise.periods[period].outcomes = {};
        }

        if (!exercise.periods[period].outcomes[session + 1]) {
          exercise.periods[period].outcomes[session + 1] = {};
        }

        _.times(exercise.numberOfSets[session]).forEach((set) => {
          if (!exercise.periods[period].outcomes[session + 1][set]) {
            exercise.periods[period].outcomes[session + 1] = {
              ...exercise.periods[period].outcomes[session + 1],
              [set]: {
                target: null,
                comment: null,
                video: null,
              },
            };
          }
        });
      }
    });
  });
};

export const getBlockData = (
  weekExercises,
  from,
  to,
  startingCalendarDay,
  intensityMeasure,
  activeUser,
  withWeight,
  fromMetric
) => {
  const exercisesByName = groupExercisesByName(weekExercises);
  const weeklyPeriods = getWeeklyDates(from, to, startingCalendarDay);

  getBlockExerciseData(
    weekExercises,
    exercisesByName,
    startingCalendarDay,
    intensityMeasure,
    activeUser,
    withWeight,
    fromMetric
  );

  addMissingPeriods(exercisesByName, { ...weeklyPeriods });
  addMissingOutcomes(exercisesByName);

  return exercisesByName;
};

export const getPeriods = (from, to, startingCalendarDay) => {
  const weeklyPeriods = [];

  let { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
    from,
    startingCalendarDay
  );

  while (endOfWeek.isSameOrBefore(moment(to).add(7, "days"))) {
    const period = `${startOfWeek.format("MMM D")} - ${moment(endOfWeek).format(
      "MMM D"
    )}`;

    weeklyPeriods.push(period);

    startOfWeek.add(7, "days");
    endOfWeek.add(7, "days");
  }

  return weeklyPeriods;
};

/**
 * Calendar week columns for a date range (Block Review table + Program Insights).
 * @param {string} from
 * @param {string} to
 * @param {number} [startingCalendarDay]
 * @returns {{ periodKey: string, label: string, startOfWeek: string, endOfWeek: string }[]}
 */
export const listCalendarWeekPeriods = (from, to, startingCalendarDay) => {
  const weeklyPeriods = getWeeklyDates(from, to, startingCalendarDay);
  const labels = getPeriods(from, to, startingCalendarDay);

  return Object.keys(weeklyPeriods).map((periodKey, index) => ({
    periodKey,
    label: labels[index],
    startOfWeek: weeklyPeriods[periodKey].startOfWeek,
    endOfWeek: weeklyPeriods[periodKey].endOfWeek,
  }));
};

const getMetricsExerciseData = (
  weekExercises,
  startingCalendarDay,
  exerciseFilters
) => {
  let periods = {};

  for (const exercise of weekExercises) {
    if (exerciseFilters.length > 0) {
      const name = getExerciseName(exercise);

      if (!exerciseFilters.includes(name)) {
        continue;
      }
    }

    const { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
      String(exercise.utcDate),
      startingCalendarDay
    );

    const period = `${startOfWeek.format("YYYY-MM-DD")} - ${moment(
      endOfWeek
    ).format("YYYY-MM-DD")}`;

    // Create object
    if (!periods[period]) {
      periods[period] = {
        startOfWeek: startOfWeek.toISOString(),
        volume: 0,
        sets: {},
        reps: {},
      };
    }

    // For complex exercises, use the first exercise's muscle groups
    const baseExercise = exercise.exercises ? exercise.exercises[0] : exercise;
    const { primaryMuscleGroup, secondaryMuscleGroup } = baseExercise;

    let totalSets = 0;
    let totalReps = 0;

    // For complex exercises, aggregate data from all nested exercises
    if (exercise.exercises && Array.isArray(exercise.exercises)) {
      exercise.exercises.forEach((nestedExercise) => {
        nestedExercise.sets.forEach((set) => {
          let volume = 0;
          let repsSum = 0;
          let setsSum = 0;

          if (set.outcomes) {
            const keys = Object.keys(set.outcomes);

            keys.forEach((key) => {
              if (set.outcomes[key].outcome) {
                const { status } = set.outcomes[key];

                if (status === COMPLETED) {
                  const { reps, load } = set.outcomes[key].outcome;
                  volume += Math.round(Number(load) * Number(reps) || 0);
                  repsSum += Number(reps) || 0;
                  setsSum++;
                }
              }
            });
          }

          periods[period].volume += volume;
          totalSets += setsSum;
          totalReps += repsSum;
        });
      });
    } else {
      // Regular exercise processing
      const setsToProcess = exercise.sets;

      setsToProcess.forEach((set) => {
        let volume = 0;
        let repsSum = 0;
        let setsSum = 0;

        if (set.outcomes) {
          const keys = Object.keys(set.outcomes);

          keys.forEach((key) => {
            if (set.outcomes[key].outcome) {
              const { status } = set.outcomes[key];

              if (status === COMPLETED) {
                const { reps, load } = set.outcomes[key].outcome;
                volume += Math.round(Number(load) * Number(reps) || 0);
                repsSum += Number(reps) || 0;
                setsSum++;
              }
            }
          });
        }

        periods[period].volume += volume;
        totalSets += setsSum;
        totalReps += repsSum;
      });
    }

    if (primaryMuscleGroup) {
      if (!periods[period].sets[primaryMuscleGroup]) {
        periods[period].sets[primaryMuscleGroup] = 0;
      }
      if (!periods[period].reps[primaryMuscleGroup]) {
        periods[period].reps[primaryMuscleGroup] = 0;
      }

      const numberOfSets = periods[period].sets[primaryMuscleGroup] || 0;
      const numberOfReps = periods[period].reps[primaryMuscleGroup] || 0;

      periods[period].sets[primaryMuscleGroup] = numberOfSets + totalSets || 0;
      periods[period].reps[primaryMuscleGroup] = numberOfReps + totalReps || 0;
    }

    if (secondaryMuscleGroup && secondaryMuscleGroup !== primaryMuscleGroup) {
      if (!periods[period].sets[secondaryMuscleGroup]) {
        periods[period].sets[secondaryMuscleGroup] = 0;
      }
      if (!periods[period].reps[secondaryMuscleGroup]) {
        periods[period].reps[secondaryMuscleGroup] = 0;
      }

      const numberOfSets = periods[period].sets[secondaryMuscleGroup] || 0;
      const numberOfReps = periods[period].reps[secondaryMuscleGroup] || 0;

      periods[period].sets[secondaryMuscleGroup] =
        numberOfSets + totalSets || 0;
      periods[period].reps[secondaryMuscleGroup] =
        numberOfReps + totalReps || 0;
    }
  }

  return periods;
};

const fillMissingKeys = (obj) => {
  const result = {};
  MUSCLE_GROUP_ARRAY.forEach((muscle) => {
    if (muscle !== "None") {
      result[muscle] = obj[muscle] || 0;
    }
  });
  // Legacy: "Legs" was removed from the selector but may still exist in stored data.
  // Count it toward both Quadriceps and Hamstrings so it keeps appearing in the table.
  if (obj["Legs"]) {
    result["Quadriceps"] = (result["Quadriceps"] || 0) + obj["Legs"];
    result["Hamstrings"] = (result["Hamstrings"] || 0) + obj["Legs"];
  }
  return result;
};

export const getMetricsData = (
  weekExercises,
  startingCalendarDay,
  from,
  to,
  exerciseFilters = []
) => {
  const metricsData = getMetricsExerciseData(
    weekExercises,
    startingCalendarDay,
    exerciseFilters
  );

  const weeklyPeriods = getWeeklyDates(from, to, startingCalendarDay);

  Object.keys(weeklyPeriods).forEach((period) => {
    if (!metricsData.hasOwnProperty(period)) {
      metricsData[period] = {
        volume: 0,
        sets: {},
        reps: {},
        startOfWeek: weeklyPeriods[period].startOfWeek,
      };
    }
  });

  Object.keys(metricsData).forEach((period) => {
    metricsData[period].sets = fillMissingKeys(metricsData[period].sets);
    metricsData[period].reps = fillMissingKeys(metricsData[period].reps);
  });

  return metricsData;
};

/**
 * Computes the week-over-week trend for a single value.
 *
 * Returns `null` when either value is missing so the caller renders nothing
 * (PRD §5.4 / §8 — no placeholder for missing comparisons).
 *
 * @param {number|null} curr current week value
 * @param {number|null} prev previous week value (same set position / metric)
 * @param {string} metric one of "reps" | "load" | "rpe" | "e1rm" | "volume" | "maxWeight"
 * @returns {{ dir: "up"|"down"|"same", metric: string } | null}
 */
export const getTrend = (curr, prev, metric) => {
  if (curr == null || prev == null) return null;
  const currNum = Number(curr);
  const prevNum = Number(prev);
  if (Number.isNaN(currNum) || Number.isNaN(prevNum)) return null;
  const dir = currNum > prevNum ? "up" : currNum < prevNum ? "down" : "same";
  return { dir, metric };
};

/**
 * Scans earlier weeks (sortedPeriods is ascending) and returns the most recent
 * non-null value strictly before currentIndex, or null if none exists in range.
 * Used to compare a set/metric against the last week that actually logged it,
 * rather than only the immediately-preceding week.
 *
 * @param {Array<[string, object]>} sortedPeriods `[periodKey, periodData]` entries, ascending
 * @param {number} currentIndex index of the current week within sortedPeriods
 * @param {(periodData: object) => number|null} getValue extracts the value for a week
 * @returns {number|null}
 */
export const findPreviousValue = (sortedPeriods, currentIndex, getValue) => {
  for (let i = currentIndex - 1; i >= 0; i -= 1) {
    const value = getValue(sortedPeriods[i][1]);
    if (value != null) return value;
  }
  return null;
};
